/**
 * Utilitários para aplicar cores dinâmicas da igreja nos componentes
 */

/**
 * Converte uma cor hexadecimal para RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null
}

/**
 * Converte RGB para HSL
 */
export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h: number, s: number
  const l = (max + min) / 2

  if (max === min) {
    h = s = 0 // achromatic
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
      default:
        h = 0
    }

    h /= 6
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

/**
 * Gera variações de uma cor (mais clara e mais escura)
 */
export function generateColorVariations(hex: string) {
  const rgb = hexToRgb(hex)
  if (!rgb) return null

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b)

  // Gera variações mais claras e mais escuras
  const variations = {
    50: `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(95, hsl.l + 40)}%)`,
    100: `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(90, hsl.l + 30)}%)`,
    200: `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(85, hsl.l + 20)}%)`,
    300: `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(80, hsl.l + 10)}%)`,
    400: `hsl(${hsl.h}, ${hsl.s}%, ${Math.min(75, hsl.l + 5)}%)`,
    500: hex, // Cor original
    600: `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(25, hsl.l - 5)}%)`,
    700: `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(20, hsl.l - 10)}%)`,
    800: `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(15, hsl.l - 20)}%)`,
    900: `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(10, hsl.l - 30)}%)`,
    950: `hsl(${hsl.h}, ${hsl.s}%, ${Math.max(5, hsl.l - 40)}%)`,
  }

  return variations
}

/**
 * Aplica as cores da igreja como variáveis CSS customizadas
 */
export function applyChurchColors(primaryColor: string, secondaryColor: string) {
  console.log('🎨 Aplicando cores da igreja:', { primaryColor, secondaryColor })
  
  const primaryVariations = generateColorVariations(primaryColor)
  const secondaryVariations = generateColorVariations(secondaryColor)

  if (!primaryVariations || !secondaryVariations) {
    console.log('❌ Erro ao gerar variações de cores')
    return
  }

  const root = document.documentElement

  // Aplica as variações da cor primária
  Object.entries(primaryVariations).forEach(([shade, color]) => {
    root.style.setProperty(`--church-primary-${shade}`, color)
  })

  // Aplica as variações da cor secundária
  Object.entries(secondaryVariations).forEach(([shade, color]) => {
    root.style.setProperty(`--church-secondary-${shade}`, color)
  })

  // Define as cores principais
  root.style.setProperty('--church-primary', primaryColor)
  root.style.setProperty('--church-secondary', secondaryColor)

  // Sobrescreve as variáveis CSS do Tailwind para aplicar as cores da igreja
  const primaryRgb = hexToRgb(primaryColor)
  const secondaryRgb = hexToRgb(secondaryColor)
  
  if (primaryRgb) {
    // Converte para o formato HSL que o Tailwind usa
    const primaryHsl = rgbToHsl(primaryRgb.r, primaryRgb.g, primaryRgb.b)
    // A função rgbToHsl já retorna valores corretos (h: 0-360, s: 0-100, l: 0-100)
    const primaryHslString = `${Math.round(primaryHsl.h)} ${Math.round(primaryHsl.s)}% ${Math.round(primaryHsl.l)}%`
    
    console.log('🔵 Aplicando cor primária:', primaryHslString)
    root.style.setProperty('--primary', primaryHslString)
    root.style.setProperty('--primary-foreground', '210 40% 98%') // Texto branco para contraste
  }
  
  if (secondaryRgb) {
    const secondaryHsl = rgbToHsl(secondaryRgb.r, secondaryRgb.g, secondaryRgb.b)
    // A função rgbToHsl já retorna valores corretos (h: 0-360, s: 0-100, l: 0-100)
    const secondaryHslString = `${Math.round(secondaryHsl.h)} ${Math.round(secondaryHsl.s)}% ${Math.round(secondaryHsl.l)}%`
    
    console.log('🟡 Aplicando cor secundária:', secondaryHslString)
    root.style.setProperty('--secondary', secondaryHslString)
    root.style.setProperty('--secondary-foreground', '210 40% 98%') // Texto branco para contraste
  }
  
  console.log('✅ Cores aplicadas com sucesso!')
}

/**
 * Remove as cores customizadas da igreja
 */
export function removeChurchColors() {
  const root = document.documentElement
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950']

  shades.forEach((shade) => {
    root.style.removeProperty(`--church-primary-${shade}`)
    root.style.removeProperty(`--church-secondary-${shade}`)
  })

  root.style.removeProperty('--church-primary')
  root.style.removeProperty('--church-secondary')
  
  // Remove as variáveis do Tailwind (restaura para os valores padrão)
  root.style.removeProperty('--primary')
  root.style.removeProperty('--primary-foreground')
  root.style.removeProperty('--secondary')
  root.style.removeProperty('--secondary-foreground')
}

/**
 * Gera classes CSS dinâmicas para as cores da igreja
 */
export function getChurchColorClasses(primaryColor: string, secondaryColor: string) {
  return {
    // Classes para cor primária
    primary: {
      bg: 'bg-[var(--church-primary)]',
      text: 'text-[var(--church-primary)]',
      border: 'border-[var(--church-primary)]',
      hover: 'hover:bg-[var(--church-primary-600)]',
      focus: 'focus:ring-[var(--church-primary)]',
    },
    // Classes para cor secundária
    secondary: {
      bg: 'bg-[var(--church-secondary)]',
      text: 'text-[var(--church-secondary)]',
      border: 'border-[var(--church-secondary)]',
      hover: 'hover:bg-[var(--church-secondary-600)]',
      focus: 'focus:ring-[var(--church-secondary)]',
    },
    // Estilos inline para casos específicos
    inline: {
      primary: { backgroundColor: primaryColor, color: 'white' },
      secondary: { backgroundColor: secondaryColor, color: 'white' },
      primaryText: { color: primaryColor },
      secondaryText: { color: secondaryColor },
    },
  }
}